import { motion } from "framer-motion";
import { fadeUp, stagger } from "@/lib/motion";
import { MessageCircle, Calendar, Coffee, Sprout } from "lucide-react";

const steps = [
  {
    icon: MessageCircle,
    title: "Επικοινωνία",
    desc: "Στέλνεις μήνυμα από τη φόρμα, email ή τηλέφωνο. Συνήθως απαντώ εντός της ημέρας.",
  },
  {
    icon: Calendar,
    title: "Κλείσιμο ραντεβού",
    desc: "Βρίσκουμε μαζί μία ώρα που σου ταιριάζει — δια ζώσης στην Πάτρα ή online.",
  },
  {
    icon: Coffee,
    title: "Η πρώτη μας συνάντηση",
    desc: "50 λεπτά γνωριμίας. Μιλάμε για ό,τι σε φέρνει εδώ, χωρίς κρίση και χωρίς πίεση.",
  },
  {
    icon: Sprout,
    title: "Το ταξίδι σου",
    desc: "Αν αισθανθείς άνετα, ορίζουμε στόχους και ρυθμό. Καμία δέσμευση εκτός εάν εσύ το θελήσεις.",
  },
];

export default function FirstSession() {
  return (
    <section
      id="first-session"
      data-testid="first-session-section"
      className="py-24 sm:py-32 bg-primary/[0.05]"
    >
      <div className="max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20">
        <motion.div {...fadeUp} className="max-w-[720px] mb-16">
          <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5">
            Πώς ξεκινάμε
          </p>
          <h2
            data-testid="first-session-heading"
            className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]"
          >
            Η πρώτη σου <span className="italic font-light">συνεδρία.</span>
          </h2>
          <p className="mt-6 font-body font-light text-lg text-foreground/70 leading-relaxed">
            Το πιο δύσκολο βήμα σε μια θεραπεία είναι το πρώτο. Ας κάνουμε αυτό
            το βήμα λίγο πιο ξεκάθαρο — να ξέρεις τι να περιμένεις.
          </p>
        </motion.div>

        <motion.ol
          {...stagger(0.09)}
          className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.li
                key={s.title}
                variants={{
                  initial: { opacity: 0, y: 26 },
                  whileInView: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                  },
                }}
                data-testid={`first-session-step-${i}`}
                className="relative bg-background rounded-3xl p-7 border border-border/50"
              >
                <div className="flex items-baseline justify-between mb-6">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <span className="font-heading italic text-muted-foreground/60 text-lg">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-heading text-2xl text-foreground tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed text-base">
                  {s.desc}
                </p>
              </motion.li>
            );
          })}
        </motion.ol>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-12 max-w-[640px] font-body font-light text-foreground/75 italic leading-relaxed"
        >
          «Δεν χρειάζεται να ξέρεις τι θα πεις. Δεν χρειάζεται να είσαι έτοιμος/η.
          Χρειάζεται μόνο να είσαι εδώ.»
        </motion.p>
      </div>
    </section>
  );
}
